import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { FoundOperation, useGetApiCatalogOperationsGroupQuery, useGetApiCatalogOperationsQuery } from "api/generatedApi";
import { useOperationTreeData, TreeNode } from "./hooks/use-operation-tree-data";



import { Stack } from "@mui/material";

export interface Props {
  searchResults: FoundOperation[];
  selectedOperationProps: (operation: FoundOperation) => void;
}

export function SelectOperationTree(props: Props) {
  const { searchResults, selectedOperationProps } = props;
  const { t } = useTranslation();

  const { data: operationsGroups } = useGetApiCatalogOperationsGroupQuery();
  const { data: operations } = useGetApiCatalogOperationsQuery();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>("");

  // Transform data using custom hook
  const { treeData } = useOperationTreeData({
    operationGroups: operationsGroups?.operationGroups || [],
    operations: operations?.operations || [],
    searchResults: searchResults,
  });



  const formatOperationTime = (milliseconds: number): string => {
    const hours = Math.floor(milliseconds / 3600000);
    const minutes = Math.floor((milliseconds % 3600000) / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Auto-expand tree when search results change
  useEffect(() => {
    if (searchResults.length > 0) {
      // Expand all groups to show filtered results
      const allGroupIds: string[] = [];
      const extractGroupIds = (nodes: TreeNode[]) => {
        nodes.forEach((node) => {
          if (node.type === "group") {
            allGroupIds.push(node.id);
            if (node.children) {
              extractGroupIds(node.children);
            }
          }
        });
      };
      extractGroupIds(treeData);
      setExpanded(allGroupIds);
    }
  }, [searchResults, treeData]);

  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };

  const handleSelect = (event: React.SyntheticEvent, nodeId: string) => {
    const findNode = (nodes: TreeNode[], id: string): TreeNode | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = findNode(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const selectedNode = findNode(treeData, nodeId);


    if (selectedNode && selectedNode.type === "operation" && selectedNode.operationData) {
      setSelected(nodeId);
      const selectedOperation: FoundOperation = {
        operationId: selectedNode.operationData.operationId,
        operationNumber: selectedNode.operationData.operationNumber,
        operationText: selectedNode.operationData.operationText,
      };
      selectedOperationProps(selectedOperation);
    }
  };

  const renderTree = (node: TreeNode) => {
    const isGroup = node.type === "group";
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded.includes(node.id);

    const nodeIcon = isGroup ? isExpanded ? <FolderOpenOutlinedIcon sx={{ mr: 1, fontSize: "20px" }} /> : <FolderOutlinedIcon sx={{ mr: 1, fontSize: "20px" }} /> : <InsertDriveFileOutlinedIcon sx={{ mr: 1, fontSize: "18px" }} />;

    return (
      <TreeItem
        key={node.id}
        nodeId={node.id}
        data-testid={`tree-${node.type}-${node.name}`}
        label={
          <Box
            sx={{
              py: 0.5,
              display: "flex",
            }}
          >
            <Box>{nodeIcon}</Box>
            <Stack>
              <Typography
                variant="body2"
                sx={{
                  flexGrow: 1,
                  fontWeight: isGroup ? 500 : 400,
                  color: "text.primary",
                }}
              > {/*String refactor to prettify text*/}
                {node.name.charAt(0).toUpperCase() + node.name.slice(1).toLowerCase()}
              </Typography>

              {!isGroup && node.operationData && node.operationData.operationTime && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.main",
                  }}
                >
                  {formatOperationTime(node.operationData.operationTime)}
                </Typography>
              )}
            </Stack>
          </Box>
        }
      >
        {hasChildren && node.children!.map((child) => renderTree(child))}
      </TreeItem>
    );
  };

  if (treeData.length === 0) {
    return (
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
          {t("content.measurements.create.selectOperationStep.noResults")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, overflowY: "auto" }}>
      <TreeView
        aria-label="operation tree navigator"
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        expanded={expanded}
        selected={selected}
        onNodeToggle={handleToggle}
        onNodeSelect={handleSelect}
        sx={{
          "& .MuiTreeItem-content": {
            padding: "2px 8px",
            "&:hover": {
              backgroundColor: "action.hover",
            },
            "&.Mui-selected": {
              backgroundColor: "action.selected",
              "&:hover": {
                backgroundColor: "action.selected",
              },
            },
          },
          "& .MuiTreeItem-iconContainer > svg": {
            fontSize: "18px",
          },
        }}
      >
        {treeData.map((node) => renderTree(node))}
      </TreeView>
    </Box>
  );
}
